import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { getMediaBlobUrl } from '../../utils/api';
import { 
  Newspaper, Plus, Upload, Trash2, Edit, Save, Search, Filter, 
  Calendar, User, Tag, ChevronRight, Loader2, X, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import TwoStepUpload from '../../components/TwoStepUpload';
import useAuthStore from '../../store/authStore';

// Category colors
const CATEGORY_COLORS = {
  announcement: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-600' },
  event: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-600' },
  update: { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600' },
  news: { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-600' },
  default: { bg: 'bg-slate-500', light: 'bg-slate-100', text: 'text-slate-600' }
};

const AuthenticatedImage = ({ src, alt, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadImage = async () => {
      if (src) {
        try {
          const url = await getMediaBlobUrl(src);
          if (mounted) { setImageUrl(url); setLoading(false); }
        } catch (e) {
          if (mounted) { setError(true); setLoading(false); }
        }
      } else { setLoading(false); }
    };
    loadImage();
    return () => { mounted = false; if (imageUrl) window.URL.revokeObjectURL(imageUrl); };
  }, [src]);

  if (loading) return <div className={`${className} bg-slate-700 animate-pulse flex items-center justify-center`}><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>;
  if (error || !imageUrl) return <div className={`${className} bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center`}><Newspaper className="w-12 h-12 text-slate-600" /></div>;
  return <img src={imageUrl} alt={alt} className={className} />;
};

const News = () => {
  const { user } = useAuthStore();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingNews, setViewingNews] = useState(null);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('news');
  const [stagedImageUrl, setStagedImageUrl] = useState(null);
  const [stagedImageName, setStagedImageName] = useState(null);
  
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('news');
  const [editImage, setEditImage] = useState(null);

  const { getRootProps: getEditRootProps, getInputProps: getEditInputProps, isDragActive: isEditDragActive } = useDropzone({
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    maxFiles: 1,
    onDrop: (acceptedFiles) => setEditImage(acceptedFiles[0]),
  });

  useEffect(() => { loadNews(); }, []);

  const loadNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  // Filtered news
  const filteredNews = useMemo(() => {
    return news.filter(n => {
      const matchesSearch = n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           n.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || (n.category || 'news') === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [news, searchQuery, selectedCategory]);

  // Featured news (latest with image)
  const featuredNews = useMemo(() => {
    return filteredNews.find(n => n.image) || filteredNews[0];
  }, [filteredNews]);

  // Rest of news (excluding featured)
  const restNews = useMemo(() => {
    return filteredNews.filter(n => n.id !== featuredNews?.id);
  }, [filteredNews, featuredNews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/news', { title, content, category, image: stagedImageUrl, author: user.full_name });
      toast.success('News posted successfully');
      setDialogOpen(false);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post news');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSaved = (fileUrl, fileName) => {
    setStagedImageUrl(fileUrl);
    setStagedImageName(fileName);
  };

  const openEditDialog = (item) => {
    setEditingNews(item);
    setEditTitle(item.title);
    setEditContent(item.content);
    setEditCategory(item.category || 'news');
    setEditImage(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingNews) return;
    setLoading(true);
    try {
      let image_url = editingNews.image;
      if (editImage) {
        const formData = new FormData();
        formData.append('file', editImage);
        const uploadResponse = await api.post('/files/upload', formData);
        image_url = uploadResponse.data.file_url;
      }
      await api.put(`/news/${editingNews.id}`, { title: editTitle, content: editContent, category: editCategory, image: image_url });
      toast.success('News updated successfully');
      setEditDialogOpen(false);
      setEditingNews(null);
      loadNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update news');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('News deleted');
      loadNews();
    } catch (error) {
      toast.error('Failed to delete news');
    }
  };

  const resetForm = () => { setTitle(''); setContent(''); setCategory('news'); setStagedImageUrl(null); setStagedImageName(null); };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;

  return (
    <div className="space-y-6" data-testid="admin-news-page">
      <BackButton to="/admin" />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-orange-500 to-rose-600 p-4 rounded-2xl shadow-lg">
            <Newspaper className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-white">News Management</h1>
            <p className="text-slate-400">Create and manage news articles</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-rose-600 hover:from-orange-600 hover:to-rose-700 shadow-lg">
              <Plus className="w-4 h-4 mr-2" />Post News
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700 max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">Create News Article</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="text-slate-300">Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="News headline..." required className="bg-slate-700 border-slate-600 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Category</Label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white">
                    <option value="news">News</option>
                    <option value="announcement">Announcement</option>
                    <option value="event">Event</option>
                    <option value="update">Update</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Content</Label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your news content..." rows={6} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500" required />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Featured Image (Two-Step: Upload → Save)</Label>
                <TwoStepUpload 
                  onSaveComplete={handleImageSaved}
                  onCancel={() => { setStagedImageUrl(null); setStagedImageName(null); }}
                  label="Upload Featured Image"
                  acceptedTypes={{
                    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                  }}
                />
                {stagedImageUrl && (
                  <p className="text-sm text-emerald-400 mt-2">✓ Image saved: {stagedImageName}</p>
                )}
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-rose-600">{loading ? 'Posting...' : 'Post News'}</Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search news..." className="pl-10 bg-slate-700 border-slate-600 text-white" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white">
            <option value="all">All Categories</option>
            <option value="news">News</option>
            <option value="announcement">Announcements</option>
            <option value="event">Events</option>
            <option value="update">Updates</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-orange-500/20 to-rose-500/20 border border-orange-500/30 rounded-xl p-4">
          <p className="text-orange-400 text-sm font-medium">Total Articles</p>
          <p className="text-2xl font-bold text-white">{news.length}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-400 text-sm font-medium">Announcements</p>
          <p className="text-2xl font-bold text-white">{news.filter(n => n.category === 'announcement').length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-xl p-4">
          <p className="text-purple-400 text-sm font-medium">Events</p>
          <p className="text-2xl font-bold text-white">{news.filter(n => n.category === 'event').length}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <p className="text-emerald-400 text-sm font-medium">With Images</p>
          <p className="text-2xl font-bold text-white">{news.filter(n => n.image).length}</p>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Edit News Article</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white">
                  <option value="news">News</option>
                  <option value="announcement">Announcement</option>
                  <option value="event">Event</option>
                  <option value="update">Update</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Content</Label>
              <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={6} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" required />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Replace Image (Optional)</Label>
              <div {...getEditRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${isEditDragActive ? 'border-orange-500 bg-orange-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
                <input {...getEditInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                {editImage ? <p className="text-sm text-orange-400">New: {editImage.name}</p> : <p className="text-sm text-slate-400">Drop to replace</p>}
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-rose-600"><Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl bg-slate-800 text-white border-slate-700 max-h-[90vh] overflow-y-auto">
          {viewingNews && (
            <>
              <DialogHeader><DialogTitle className="text-white text-2xl">{viewingNews.title}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {viewingNews.image && <AuthenticatedImage src={viewingNews.image} alt={viewingNews.title} className="w-full h-64 object-cover rounded-xl" />}
                <div className="flex items-center space-x-4 text-sm text-slate-400">
                  <span className="flex items-center"><User className="w-4 h-4 mr-1" />{viewingNews.author}</span>
                  <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatDate(viewingNews.published_at)}</span>
                  <span className={`${getCategoryColor(viewingNews.category || 'news').bg} text-white text-xs px-2 py-1 rounded-full capitalize`}>{viewingNews.category || 'news'}</span>
                </div>
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{viewingNews.content}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* News Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
      ) : filteredNews.length > 0 ? (
        <div className="space-y-8">
          {/* Featured Article */}
          {featuredNews && (
            <article className="group relative bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 hover:border-orange-500/50 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto">
                  <AuthenticatedImage src={featuredNews.image} alt={featuredNews.title} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4">
                    <span className={`${getCategoryColor(featuredNews.category || 'news').bg} text-white text-xs px-3 py-1 rounded-full font-medium capitalize`}>
                      {featuredNews.category || 'news'}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center space-x-3 text-xs text-slate-400 mb-3">
                      <span className="flex items-center"><User className="w-3 h-3 mr-1" />{featuredNews.author}</span>
                      <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />{formatDate(featuredNews.published_at)}</span>
                    </div>
                    <h2 className="font-serif font-bold text-2xl text-white mb-3 group-hover:text-orange-400 transition-colors line-clamp-2">{featuredNews.title}</h2>
                    <p className="text-slate-400 line-clamp-4">{featuredNews.content}</p>
                  </div>
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
                    <button onClick={() => { setViewingNews(featuredNews); setViewDialogOpen(true); }} className="text-orange-400 hover:text-orange-300 flex items-center text-sm font-medium">
                      Read Full Article <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                    <div className="flex items-center space-x-1">
                      <button onClick={() => openEditDialog(featuredNews)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(featuredNews.id, featuredNews.title)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          )}

          {/* Rest of News Grid */}
          {restNews.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restNews.map((item) => {
                const catColor = getCategoryColor(item.category || 'news');
                return (
                  <article key={item.id} className="group bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 hover:border-orange-500/50 transition-all duration-300 hover:shadow-xl">
                    <div className="relative h-48">
                      <AuthenticatedImage src={item.image} alt={item.title} className="w-full h-full object-cover" />
                      <div className="absolute top-3 left-3">
                        <span className={`${catColor.bg} text-white text-xs px-2 py-1 rounded-full font-medium capitalize`}>{item.category || 'news'}</span>
                      </div>
                      <div className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditDialog(item)} className="p-2 bg-black/50 backdrop-blur text-white hover:bg-amber-500 rounded-lg"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item.id, item.title)} className="p-2 bg-black/50 backdrop-blur text-white hover:bg-red-500 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center space-x-3 text-xs text-slate-500 mb-2">
                        <span>{item.author}</span>
                        <span>•</span>
                        <span>{formatDate(item.published_at)}</span>
                      </div>
                      <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-orange-400 transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-400 line-clamp-3 mb-4">{item.content}</p>
                      <button onClick={() => { setViewingNews(item); setViewDialogOpen(true); }} className="text-orange-400 hover:text-orange-300 flex items-center text-sm font-medium">
                        Read more <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <Newspaper className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">No news articles found</p>
          <p className="text-sm text-slate-500 mt-2">{searchQuery || selectedCategory !== 'all' ? 'Try adjusting your filters' : 'Click "Post News" to create your first article'}</p>
        </div>
      )}
    </div>
  );
};

export default News;
